import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { db } from "../../firebase";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
} from "firebase/firestore";

export default function Clients() {
  // 🔹 Dummy clients used only for one-time seeding
  const dummyClients = [
    {
      fullName: "James Dean",
      address: "20c Church Road, Bookham",
      postcode: "KT23 3PW",
      daysOfWork: "Mon",
      totalHours: 5,
      timePeriods: ["10:00–15:00"],
      contact: "Toby Shaw, 07990557718, tobyshaw77@gmail.com",
      rate: 15,
    },
    {
      fullName: "Akash",
      address:
        "4 Bleinheim Gardens, Wallington, Surrey, Sutton, London",
      postcode: "SM6 9PH",
      daysOfWork: "Tue, Thu",
      totalHours: 14,
      timePeriods: ["07:30–14:30"],
      contact: "Akash",
      rate: 15,
    },
    {
      fullName: "Shuran Noman",
      address: " ",
      postcode: "CR0 6EF",
      daysOfWork: "Sat",
      totalHours: 5,
      timePeriods: ["07:30–14:30"],
      contact: "Shuran, 07549198286",
      rate: 15,
    },
    {
      fullName: "Rosemary Arnaiz",
      address:
        "Cottage 64, 4 Dunheved Road South, Thornton Heath, Croydon",
      postcode: "CR7-6AD",
      daysOfWork: "Tue",
      totalHours: 2,
      timePeriods: ["10:00–12:00"],
      contact: "",
      rate: 20,
    },
    {
      fullName: "Akash's Mum",
      address: "7 Clyde Road, Sutton, London",
      postcode: "SM6 8PZ",
      daysOfWork: "Mon",
      totalHours: 7,
      timePeriods: ["08:30–15:30"],
      contact: "",
      rate: 15,
    },
    {
      fullName: "Akash's Tenancy1",
      address: "6 Park Hill Road, Sutton, London",
      postcode: "SM6 0SB",
      daysOfWork: "Fri",
      totalHours: 2,
      timePeriods: ["10:00–12:00"],
      contact: "",
      rate: 15,
    },
    {
      fullName: "Akash's Tenancy2",
      address: "16B Hawthorn Road, Sutton, London",
      postcode: "SM6 0SX",
      daysOfWork: "Fri",
      totalHours: 2,
      timePeriods: ["12:15–14:15"],
      contact: "",
      rate: 15,
    },
    {
      fullName: "Akash's Tenancy3",
      address: "4 Milton Road, Sutton, London",
      postcode: "SM6 9RW",
      daysOfWork: "Fri",
      totalHours: 2,
      timePeriods: ["14:30–16:30"],
      contact: "",
      rate: 15,
    },
  ];

  const [clients, setClients] = useState([]);
  const [newClient, setNewClient] = useState({
    fullName: "",
    address: "",
    postcode: "",
    daysOfWork: "",
    totalHours: "",
    timePeriods: "",
    contact: "",
    rate: "",
  });

  // 🔹 Load data from Firestore on mount and seed if empty
  useEffect(() => {
    const fetchAndSeedClients = async () => {
      const clientRef = collection(db, "clients");
      const snapshot = await getDocs(clientRef);

      if (snapshot.empty) {
        console.log("Seeding dummy clients...");
        for (let client of dummyClients) {
          await addDoc(clientRef, client);
        }
        console.log("Dummy clients added!");
      }

      const updatedSnapshot = await getDocs(clientRef);
      const fetchedClients = updatedSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setClients(fetchedClients);
    };

    fetchAndSeedClients();
  }, []);

  // 🔹 Handle form changes
  const handleChange = (e) => {
    setNewClient({ ...newClient, [e.target.name]: e.target.value });
  };

  // 🔹 Add new client
  const handleAddClient = async (e) => {
    e.preventDefault();
    if (!newClient.fullName || !newClient.address)
      return alert("Please fill required fields.");

    try {
      const docRef = await addDoc(collection(db, "clients"), {
        ...newClient,
        totalHours: Number(newClient.totalHours),
        rate: Number(newClient.rate),
        timePeriods: newClient.timePeriods
          .split(",")
          .map((t) => t.trim()),
        createdAt: new Date(),
      });

      setClients([
        ...clients,
        { id: docRef.id, ...newClient },
      ]);

      setNewClient({
        fullName: "",
        address: "",
        postcode: "",
        daysOfWork: "",
        totalHours: "",
        timePeriods: "",
        contact: "",
        rate: "",
      });
    } catch (err) {
      console.error("Error adding client:", err);
      alert("Failed to save client.");
    }
  };

  // 🔹 Delete client
  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, "clients", id));
      setClients(clients.filter((c) => c.id !== id));
    } catch (error) {
      console.error("Error deleting client:", error);
    }
  };

  // 🔹 Edit client (optional)
  const handleEdit = (id) => {
    const client = clients.find((c) => c.id === id);
    setNewClient({
      ...client,
      timePeriods: client.timePeriods.join(", "),
    });
    handleDelete(id); // re-add edited client
  };

  return (
    <div
      style={{ paddingTop: "90px" }}
      className="p-6 pt-20 min-h-screen bg-gray-700/80 backdrop-blur-md text-gray-100"
    >
      <h1 className="text-3xl font-bold mb-6 text-center">
        Client Management
      </h1>

      {/* Add Client Form */}
      <form
        onSubmit={handleAddClient}
        className="bg-gray-800 rounded-xl p-6 shadow-md mb-8 max-w-3xl mx-auto"
      >
        <h2 className="text-xl font-semibold mb-4">Add New Client</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            "fullName",
            "address",
            "postcode",
            "daysOfWork",
            "totalHours",
            "timePeriods",
            "contact",
            "rate",
          ].map((field) => (
            <input
              key={field}
              name={field}
              type={field === "totalHours" ? "number" : "text"}
              value={newClient[field]}
              onChange={handleChange}
              placeholder={
                field === "timePeriods"
                  ? "Time Periods (comma separated)"
                  : field.charAt(0).toUpperCase() +
                    field.slice(1).replace(/([A-Z])/g, " $1")
              }
              className="p-3 rounded-lg border border-gray-500 bg-gray-900 focus:ring-2 focus:ring-pink-400"
            />
          ))}
        </div>

        <button
          type="submit"
          style={{
            backgroundColor: "#4b5563",
            padding: "5px",
            marginTop: "5px",
          }}
          className="mt-6 w-full bg-pink-600 hover:bg-pink-700 text-white py-3 rounded-lg shadow-md transition"
        >
          Add Client
        </button>
      </form>

      {/* Client Table */}
      <div className="overflow-x-auto overflow-y-auto max-w-full max-h-[70vh] mx-auto rounded-xl border border-gray-700">
        <table className="min-w-[400px] w-full border-collapse bg-gray-800 rounded-xl shadow-md overflow-hidden">
          <thead className="bg-gray-900 text-gray-100">
            <tr>
              <th className="p-3 text-center w-12">#</th>
              <th className="p-3 text-center">Client Name</th>
              <th className="p-3 text-center">Post Code</th>
              <th className="p-3 text-center">Total Hours</th>
              <th className="p-3 text-center">Actions</th>
            </tr>
          </thead>

          <tbody>
            {clients.map((client, index) => (
              <tr
                key={client.id}
                className="border-t border-gray-700 hover:bg-gray-700/50 transition"
              >
                <td className="p-3 text-center">{index + 1}</td>
                <td className="p-3 text-center">
                  <Link
                    to={`/admin/clients/${client.id}`}
                    className="text-pink-400 hover:text-pink-300 underline"
                  >
                    {client.fullName}
                  </Link>
                </td>
                <td className="p-3 text-center">{client.postcode}</td>
                <td className="p-3 text-center">{client.totalHours}</td>
                <td className="p-3 text-center">
                  <button
                    onClick={() => handleDelete(client.id)}
                    style={{
                      backgroundColor: "#f43f5e",
                      padding: "5px",
                    }}
                    className="text-white px-8 py-5 rounded-lg"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {clients.length === 0 && (
              <tr>
                <td
                  colSpan="5"
                  className="p-4 text-center text-gray-400"
                >
                  No clients available.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
